import React, { useRef, useState, Ref, forwardRef, useImperativeHandle } from 'react'
import {
  InputRef,
  LegacyInput,
  touchInputs,
  InputFile,
  InputFileRef,
  SelectRef,
  Select,
  makeInputStorageKey,
} from './input'
import { stubStorage } from '@/utils/env'
import { FilterableComponentOption } from './filter-option'
import { CropBox, Product, ProductBody } from 'shared/types/api'
import { nullOr } from 'shared/utils/string'
import { useVenueContext } from '@/services/venue'
import { useAppContext } from '@/pages/_app'
import { entries, values } from 'shared/utils/array'
import { Plus } from './plus'
import { CrossSm } from './cross-sm'
import { customAlphabet } from 'nanoid'
import { filterEvents, uiEvents, useDefaultObservable, useStateFromUiEvents } from '@/utils/event'
import { Trans } from 'react-i18next'
import { currencySigns } from 'shared/utils/currency-signs'
import { useCropper } from './cropper'
import { map } from 'rxjs/operators'
import { useObservable } from 'rxjs-hooks'
import { Collapse } from './collapse'
import { getStorage } from '@/utils/persistant-state-hooks'

declare global {
  interface AppUIEventMap {
    ProductFormGroupMode: {
      type: 'ProductFormGroupMode'
      newValue: 'select' | 'create'
    }
    ProductFormSubmit: {
      type: 'ProductFormSubmit'
      result: ProductFormResult
    }
    ProductFormImageSubmitInput: {
      type: 'ProductFormImageSubmitInput'
      file: File
    }
    ProductAllergenSelect: {
      type: 'ProductAllergenSelect'
      allergenName: string
      isSelected: boolean
    }
    ProductFoodRestrictionSelect: {
      type: 'ProductFoodRestrictionSelect'
      foodRestrictionName: string
      isSelected: boolean
    }
  }
}

export type ProductFormProps = {
  productGroupName?: string
  sourceProduct?: Product
}

export type ProductFormRef = {
  process(): Promise<ProductFormResult>
}

export type ProductFormResult =
  | {
      type: 'ok'
      body: ProductBody
      picture: {
        file: File
        cropBox: CropBox
      } | null
    }
  | {
      type: 'validationError'
    }

export const ProductForm = forwardRef(ProductFormWithRef)

export function ProductFormWithRef(props: ProductFormProps, ref: Ref<ProductFormRef>) {
  const { t } = useAppContext()
  const { venueName, venue, availablePreferences } = useVenueContext()!

  const currency = venue.features.currency?.code || 'RUB'

  const refs = {
    productGroupSelect: useRef<SelectRef>(null),
    productGroupNew: useRef<InputRef>(null),
    title: useRef<InputRef>(null),
    description: useRef<InputRef>(null),
    descriptionShort: useRef<InputRef>(null),
    price: useRef<InputRef>(null),
    cookingTimeMin: useRef<InputRef>(null),
    volume: useRef<InputRef>(null),
    volumeUnit: useRef<InputRef>(null),
    calories: useRef<InputRef>(null),
    proteins: useRef<InputRef>(null),
    fats: useRef<InputRef>(null),
    carbs: useRef<InputRef>(null),
    minQuantity: useRef<InputRef>(null),
  }
  const imageRef = useRef<InputFileRef>(null)

  const productPrototype: Product = {
    name: '',
    kind: 'internal',
    title: '',
    productGroupName: '',
    description: '',
    descriptionShort: '',
    price: 0,
    volume: [],
    isUsingVolumeUnitsForQuantity: false,
    priority: 0,
    pictures: [],
    ingredients: [],
    components: [],
    allergenNames: [],
    foodRestrictionNames: [],
    upsale: {
      onCheckout: false,
      products: [],
    },
    orderModes: [],
    isInStopList: false,
    minQuantity: 1,
    modifierGroups: [],
    features: {},
  }

  const sourceProduct = props.sourceProduct ?? productPrototype

  const [isUsingVolumeUnitsForQuantity, setIsUsingVolumeUnitsForQuantity] = useState(
    sourceProduct?.isUsingVolumeUnitsForQuantity || false,
  )

  const productKey = props.sourceProduct
    ? `${venueName}/${props.sourceProduct.productGroupName}/${props.sourceProduct.name}`
    : `${venueName}/${props.productGroupName ?? 'new'}/new`

  const volumeUnitStorage = getStorage<string>(
    makeInputStorageKey(`product/${productKey}/volumeUnit`),
    sessionStorage,
  )
  const volumeUnitStorageValue = volumeUnitStorage.get() || ''

  const [volumeUnitState, setVolumeUnitState] = useState(volumeUnitStorageValue)

  const [volumeState, setVolumeState] = useState('')

  const fullDescriptionIsSameAsShort = sourceProduct.description === sourceProduct.descriptionShort
  const [hideFullDescription, setHideFullDescription] = useState(fullDescriptionIsSameAsShort)

  const productGroupMode = useDefaultObservable(
    useStateFromUiEvents({
      id: 'productForm/productGroupMode',
      storage: stubStorage,
      scope: 'local',
      startingValue: props.productGroupName ? ('select' as const) : ('create' as const),
      process: (source, scan) => {
        return source.pipe(
          scan((state, event) => {
            switch (event.type) {
              case 'ProductFormGroupMode':
                return event.newValue

              default:
                return state
            }
          }),
        )
      },
    }),
  )

  const cropperId = 'productForm'
  const cropper = useCropper({
    cropperId,
    aspectRatio: 1,
    input$: uiEvents.pipe(
      filterEvents(['ProductFormImageSubmitInput']),
      map((event) => {
        return event.file
      }),
    ),
  })

  const croppedProductPicture = useObservable(() => {
    return cropper.imageReady$
  })

  const initialSelectedAllergens = Object.fromEntries(
    availablePreferences.allAllergens.map((allergen) => {
      return [allergen.name, !!sourceProduct.allergenNames?.includes(allergen.name)] as const
    }),
  )

  const selectedAllergens$ = useStateFromUiEvents({
    id: `product/${sourceProduct.name}/allergens`,
    startingValue: initialSelectedAllergens,
    scope: 'local',
    storage: stubStorage,
    process: (source, scan) => {
      return source.pipe(
        scan((currentSelectedAllergens, event) => {
          switch (event.type) {
            case 'ProductAllergenSelect':
              return {
                ...currentSelectedAllergens,
                [event.allergenName]: !currentSelectedAllergens[event.allergenName],
              }
            default:
              return currentSelectedAllergens
          }
        }),
      )
    },
  })
  const selectedAllergens = useDefaultObservable(selectedAllergens$)

  const initialSelectedFoodRestrictions = Object.fromEntries(
    availablePreferences.allFoodRestrictions.map((foodRestriction) => {
      return [
        foodRestriction.name,
        !!sourceProduct.foodRestrictionNames?.includes(foodRestriction.name),
      ] as const
    }),
  )

  const selectedFoodRestrictions$ = useStateFromUiEvents({
    id: `product/${sourceProduct.name}/foodRestrictions`,
    startingValue: initialSelectedFoodRestrictions,
    scope: 'local',
    storage: stubStorage,
    process: (source, scan) => {
      return source.pipe(
        scan((currentSelectedFoodRestrictions, event) => {
          switch (event.type) {
            case 'ProductFoodRestrictionSelect':
              return {
                ...currentSelectedFoodRestrictions,
                [event.foodRestrictionName]:
                  !currentSelectedFoodRestrictions[event.foodRestrictionName],
              }
            default:
              return currentSelectedFoodRestrictions
          }
        }),
      )
    },
  })
  const selectedFoodRestrictions = useDefaultObservable(selectedFoodRestrictions$)

  useImperativeHandle(ref, () => {
    return {
      async process() {
        const getResult = async () => {
          const isValid = touchInputs(values(refs)).isValid
          if (!isValid) {
            return {
              type: 'validationError',
            } as const
          }

          const toInteger = (input: string) => {
            return Number.parseInt(input, 10)
          }
          const toFloat = (input: string) => {
            return Number.parseFloat(input.replace(',', '.'))
          }

          const nanoid = customAlphabet('abcdefghijklmnopqrstuvwxyz-', 10)
          const body: ProductBody = {
            title: refs.title.current!.value.trim(),
            productGroupName: refs.productGroupSelect.current?.value,
            productGroup: refs.productGroupNew.current
              ? {
                  name: nanoid(),
                  title: refs.productGroupNew.current.value,
                }
              : undefined,
            descriptionShort: refs.descriptionShort.current!.value.trim(),
            description: hideFullDescription
              ? refs.descriptionShort.current!.value.trim()
              : refs.description.current!.value.trim(),
            price: toFloat(refs.price.current!.value),
            cookingTimeMin: nullOr(refs.cookingTimeMin.current!.value, toInteger),
            volume: refs.volume.current!.implicitValue,
            volumeUnit: refs.volumeUnit.current!.value.trim() || null,
            calories: nullOr(refs.calories.current!.value, toFloat),
            proteins: nullOr(refs.proteins.current!.value, toFloat),
            fats: nullOr(refs.fats.current!.value, toFloat),
            carbs: nullOr(refs.carbs.current!.value, toFloat),
            isUsingVolumeUnitsForQuantity,
            minQuantity: toInteger(refs.minQuantity.current!.value),
            mainPhoto: null,
            isInStopList: sourceProduct.isInStopList,
            selectedAllergenNames: entries(selectedAllergens).flatMap(
              ([allergenName, isSelected]) => {
                return isSelected ? [allergenName] : []
              },
            ),
            selectedFoodRestrictionNames: entries(selectedFoodRestrictions).flatMap(
              ([foodRestrictionName, isSelected]) => {
                return isSelected ? [foodRestrictionName] : []
              },
            ),
            features: sourceProduct.features,
          }

          return {
            type: 'ok',
            body,
            picture: croppedProductPicture,
          } as const
        }

        const result = await getResult()
        uiEvents.next({
          type: 'ProductFormSubmit',
          result,
        })
        return result
      },
    }
  })

  return (
    <div>
      <div className="px-5">
        <InputFile
          ref={imageRef}
          label={t('common:mealPhoto')}
          onChange={() => {
            const file = imageRef.current!.element.files?.[0]
            if (!file) {
              return
            }
            if (cropper.isImageUploading) {
              return
            }

            // we need to clear out the input because the onChange won't get triggered if we
            // submit the same file as previously (because the input doesn't "change" in that case)
            // eslint-disable-next-line immutable/no-mutation
            imageRef.current!.element.value = ''

            uiEvents.next({ type: 'ProductFormImageSubmitInput', file })
          }}
        />
        {(() => {
          if (!props.sourceProduct)
            return (
              <div className="text-opaque dark:text-opaqueDark mt-1 leading-tight text-sm px-3">
                <Trans
                  t={t}
                  i18nKey="productForm:menuWithPhotosLooksMuchBetterYouCanAddPhotoLater"
                  components={{
                    br: <br />,
                  }}
                />
              </div>
            )
        })()}

        <div className="w-full text-opaque dark:text-opaqueDark"></div>
        <div className="flex mt-4">
          {(() => {
            switch (productGroupMode) {
              case 'select':
                return (
                  <>
                    <div className="flex-grow">
                      <Select
                        ref={refs.productGroupSelect}
                        id={`product/${productKey}/productGroup`}
                        label={t('common:category')}
                        required
                        storage={stubStorage}
                        options={venue.productGroups.map((pg) => {
                          return { id: pg.name, text: pg.title }
                        })}
                        initialOptionId={props.productGroupName}
                      />
                    </div>
                    <div className="mt-6">
                      <button
                        type="button"
                        className={`flex justify-center items-center text-lg ml-3 w-16 align-middle border border-action dark:border-actionDark rounded focus:outline-none`}
                        style={{ height: '53px' }}
                        onClick={() => {
                          uiEvents.next({
                            type: 'ProductFormGroupMode',
                            newValue: 'create',
                          })
                        }}
                      >
                        <Plus color="inline stroke-primary dark:stroke-primaryDark" />
                      </button>
                    </div>
                  </>
                )
              case 'create':
                return (
                  <>
                    <div className="flex-grow">
                      <LegacyInput
                        type="text"
                        value=""
                        ref={refs.productGroupNew}
                        id={`product/${productKey}/productGroupNew`}
                        label={t('productName:categoryName')}
                        style={{
                          height: `${53 / 16}rem`,
                        }}
                        placeholder={t('productName:enterNewCategoryName')}
                        required
                        storage={stubStorage}
                      />
                    </div>
                    {props.productGroupName && (
                      <div className="mt-6">
                        <button
                          type="button"
                          className={`flex justify-center items-center ml-3 w-16 text-center align-middle border border-action dark:border-actionDark rounded focus:outline-none`}
                          style={{ height: '53px' }}
                          onClick={() => {
                            uiEvents.next({
                              type: 'ProductFormGroupMode',
                              newValue: 'select',
                            })
                          }}
                        >
                          <CrossSm />
                        </button>
                      </div>
                    )}
                  </>
                )
            }
          })()}
        </div>
        <div className="mt-4">
          <LegacyInput
            autoFocus={props.sourceProduct === undefined}
            ref={refs.title}
            id={`product/${productKey}/title`}
            label={t('productForm:itemName')}
            required
            type="text"
            value={sourceProduct.title}
            storage={stubStorage}
            height={2}
          />
        </div>
        <div className="mt-4">
          <LegacyInput
            ref={refs.price}
            disabled={props.sourceProduct && props.sourceProduct.kind === 'internal-bridge'}
            id={`product/${productKey}/price`}
            label={`${t('common:price')}, ${currencySigns[currency as keyof typeof currencySigns]}`}
            required
            type="number"
            value={props.sourceProduct ? props.sourceProduct.price.toString() : ''}
            storage={stubStorage}
          />
        </div>
      </div>
      <div className="h-6"></div>

      <Collapse id="productForm" title={t('common:optionalFields')}>
        <div className="mt-4">
          <LegacyInput
            ref={refs.descriptionShort}
            id={`product/${productKey}/descriptionShort`}
            label={t('common:shortDescription')}
            type="text"
            value={sourceProduct.descriptionShort}
            storage={stubStorage}
            height={3}
          />
        </div>
        <div>
          <FilterableComponentOption
            marker="checkmark"
            selected={hideFullDescription}
            title={t('productForm:detailedDescriptionSameAsShort')}
            onSelected={(isSelected) => {
              setHideFullDescription(isSelected)
            }}
            addSeparator={false}
          />
        </div>
        {!hideFullDescription && (
          <div className="mb-4">
            <LegacyInput
              ref={refs.description}
              id={`product/${productKey}/description`}
              label={t('productForm:detailedDescription')}
              type="text"
              value={sourceProduct.description}
              storage={stubStorage}
              height={3}
            />
          </div>
        )}
        <div className="flex">
          <div className="flex-shrink-0"></div>
          <div>
            <LegacyInput
              ref={refs.volume}
              id={`product/${productKey}/volume`}
              label={t('common:volume')}
              type="text"
              required={volumeUnitState !== ''}
              value={sourceProduct.volume.length > 0 ? sourceProduct.volume.join(' / ') : ''}
              storage={stubStorage}
              onChange={(input) => {
                const allowedChars = /^[0-9\/\s]+$/

                const letterlessInput = input
                  .trimStart()
                  .split('')
                  .filter((letter) => {
                    return letter.match(allowedChars)
                  })
                  .join('')

                const asArray = letterlessInput
                  .split(/[\/\s]+/)
                  .filter((s) => {
                    return s.length > 0
                  })
                  .map((x) => {
                    return Number.parseInt(x, 10)
                  })

                setVolumeState(letterlessInput)

                return [letterlessInput, asArray]
              }}
            />
          </div>
          <div className="w-4 flex-shrink-0"></div>
          <div>
            <LegacyInput
              ref={refs.volumeUnit}
              id={`product/${productKey}/volumeUnit`}
              label={t('productForm:units')}
              type="text"
              required={volumeState !== ''}
              value={sourceProduct.volumeUnit?.toString() || ''}
              storage={sessionStorage}
              onChange={(input) => {
                const newState = input.toLowerCase()
                setVolumeUnitState(newState)
                return [newState]
              }}
            />
          </div>
        </div>
        <div>
          <FilterableComponentOption
            marker="checkmark"
            selected={isUsingVolumeUnitsForQuantity}
            title={t('productForm:weighingItem')}
            onSelected={async (isSelected) => {
              setIsUsingVolumeUnitsForQuantity(isSelected)
            }}
            addSeparator={false}
          />
        </div>
        <div>
          <LegacyInput
            ref={refs.cookingTimeMin}
            id={`product/${productKey}/cookingTimeMin`}
            label={t('productForm:cookingTime')}
            type="number"
            value={sourceProduct.cookingTimeMin?.toString() || ''}
            storage={stubStorage}
          />
        </div>
        <div className="flex mt-4">
          <div>
            <LegacyInput
              ref={refs.calories}
              id={`product/${productKey}/calories`}
              label={t('productForm:calories')}
              type="number"
              value={sourceProduct.calories?.toString() || ''}
              storage={stubStorage}
            />
          </div>
          <div className="w-4 flex-shrink-0"></div>
          <div>
            <LegacyInput
              ref={refs.proteins}
              id={`product/${productKey}/proteins`}
              label={t('productForm:proteins')}
              type="number"
              value={sourceProduct.proteins?.toString() || ''}
              storage={stubStorage}
            />
          </div>
          <div className="w-4 flex-shrink-0"></div>
          <div>
            <LegacyInput
              ref={refs.fats}
              id={`product/${productKey}/fats`}
              label={t('productForm:fats')}
              type="number"
              value={sourceProduct.fats?.toString() || ''}
              storage={stubStorage}
            />
          </div>
          <div className="w-4 flex-shrink-0"></div>
          <div>
            <LegacyInput
              ref={refs.carbs}
              id={`product/${productKey}/carbs`}
              label={t('productForm:carbohydrates')}
              type="number"
              value={sourceProduct.carbs?.toString() || ''}
              storage={stubStorage}
            />
          </div>
        </div>
        <div className="mt-4">
          <LegacyInput
            ref={refs.minQuantity}
            id={`product/${productKey}/minQuantity`}
            label={t('productForm:minAmount')}
            required
            type="number"
            value={sourceProduct.minQuantity.toString()}
            storage={stubStorage}
          />
        </div>
        <div className="h-6"></div>
      </Collapse>

      <Collapse id="productFoodRestrictions" title={t('common:foodRestrictions')}>
        {availablePreferences.allFoodRestrictions.map((foodRestriction, idx) => {
          const isLastItem = idx === availablePreferences.allFoodRestrictions.length - 1
          return (
            <FilterableComponentOption
              selected={!!selectedFoodRestrictions[foodRestriction.name]}
              marker="checkmark"
              title={<span dangerouslySetInnerHTML={{ __html: t(foodRestriction.title) }} />}
              key={foodRestriction.name}
              addSeparator={!isLastItem}
              onSelected={(isSelected) => {
                uiEvents.next({
                  type: 'ProductFoodRestrictionSelect',
                  foodRestrictionName: foodRestriction.name,
                  isSelected,
                })
              }}
            />
          )
        })}
      </Collapse>

      <Collapse id="productAllergens" title={t('common:allergens')}>
        {availablePreferences.allAllergens.map((allergen, idx) => {
          const isLastItem = idx === availablePreferences.allAllergens.length - 1
          return (
            <FilterableComponentOption
              selected={!!selectedAllergens[allergen.name]}
              marker="checkmark"
              title={<span dangerouslySetInnerHTML={{ __html: t(allergen.title) }} />}
              key={allergen.name}
              addSeparator={!isLastItem}
              onSelected={(isSelected) => {
                uiEvents.next({
                  type: 'ProductAllergenSelect',
                  allergenName: allergen.name,
                  isSelected,
                })
              }}
            />
          )
        })}
      </Collapse>
    </div>
  )
}
